import { LayoutParams, LayoutResult, TileStyle, PipShape, PipPosition } from './types';
import {
  BORDER_RADIUS_8,
  NO_RADIUS,
  TARGET_TILE_AR,
  GRID_DISTRIBUTION_SQUARE,
  GRID_DISTRIBUTION_SQUARE_FILL,
  SQUARE_SHARED_FILL_DISTRIBUTION,
  SQUARE_SHARED_FILL_SPEAKER_BAND_FRACTION,
  SQUARE_FILM_STRIP_TILE_HEIGHT_FRACTION,
  SQUARE_PIP_WIDTH_FRACTION
} from './constant';
import { tile, incompleteRow, buildSpeakerGrid, getContainedScreenRect, pipRectPosition } from './utils';

// ═══════════════════════════════════════════════════════════════════════════
// Layout computers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Equal-height grid fill for the square container.
 *
 * Landscape's FILL scales row heights from 16:9 AR to fill the container
 * vertically. At AR 1:1 that makes the top row of a [2,3] grid ~50% taller
 * than the bottom, which Figma §A avoids by specifying equal row heights.
 * Incomplete last rows are centred at the preceding row's tile width.
 */
function fillRect(
  rows: number[],
  top: number,
  left: number,
  width: number,
  height: number,
  gap: number,
  W: number,
  H: number,
  radius: string
): TileStyle[] {
  const rowH = (height - (rows.length - 1) * gap) / rows.length;
  const { lastIdx, isIncomplete, tileW: incompleteTileW } = incompleteRow(rows, gap, width);
  const tiles: TileStyle[] = [];
  let topOffset = top;

  for (let r = 0; r < rows.length; r++) {
    const cols = rows[r];
    const incomplete = r === lastIdx && isIncomplete;
    const cellW = incomplete ? incompleteTileW : (width - (cols - 1) * gap) / cols;
    const rowW = cols * cellW + (cols - 1) * gap;
    const leftStart = incomplete ? left + (width - rowW) / 2 : left;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftStart + c * (cellW + gap), cellW, rowH, W, H, radius));
    }
    topOffset += rowH + gap;
  }

  return tiles;
}

function computeFill(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  const dist = GRID_DISTRIBUTION_SQUARE_FILL[Math.min(count, 24)] || GRID_DISTRIBUTION_SQUARE_FILL[24];
  return fillRect(dist, margin, margin, usableW, usableH, gap, W, H, radius);
}

function computeTiles(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const dist = GRID_DISTRIBUTION_SQUARE[Math.min(count, 24)] || GRID_DISTRIBUTION_SQUARE[24];
  return buildSpeakerGrid(
    dist,
    gap,
    W - 2 * margin,
    H - 2 * margin,
    margin,
    margin,
    W,
    H,
    radius,
    1 // scaleCap: maintain 16:9, never stretch tiles to fill
  );
}

/**
 * SHARED_FILL – speaker band stacked above a contained screen share.
 *
 * §C layout: speakers on top, screen on bottom, both centred as a stack.
 * speakerSide is ignored (stacked form has no left/right split).
 * tiles[0] is the screen, per the screenshare-layout convention, even though
 * it is positioned second in the visual order.
 */
function computeSharedFill(
  W: number,
  H: number,
  count: number,
  showBorders: boolean,
  roundedCorners: boolean
): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // contentH is the height available for both sections with a single gap between them.
  const contentH = usableH - gap;
  const bandH = contentH * SQUARE_SHARED_FILL_SPEAKER_BAND_FRACTION;
  // Screen is capped at 16:9 so it never fills the letterbox bars in a square container.
  const screenH = Math.min(contentH - bandH, usableW / TARGET_TILE_AR);

  const stackH = bandH + gap + screenH;
  const stackTop = margin + Math.max(0, (usableH - stackH) / 2);

  const dist = SQUARE_SHARED_FILL_DISTRIBUTION[Math.min(count, 24)] || SQUARE_SHARED_FILL_DISTRIBUTION[24];
  const bandTiles = fillRect(dist, stackTop, margin, usableW, bandH, gap, W, H, radius);

  const screenTop = stackTop + bandH + gap;
  return [tile(screenTop, margin, usableW, screenH, W, H, radius), ...bandTiles];
}

/**
 * FILM_STRIP – contained screen share above a horizontal speaker row.
 *
 * §D layout: screen on top, single speaker row below. The strip is horizontal
 * (one row of all speakers) rather than the vertical column used by landscape.
 * speakerSide is ignored.
 */
function computeFilmStrip(
  W: number,
  H: number,
  count: number,
  showBorders: boolean,
  roundedCorners: boolean
): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const tileGap = showBorders ? W * Math.min(0.048 / count, 0.012) : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // Screen is full-width and capped at 16:9 so it never letterbox-fills the square.
  const screenH = Math.min(usableW / TARGET_TILE_AR, usableH);
  // §D tile height ceiling (200/900 of stage height), further limited by remaining space.
  const maxTileH = Math.min(H * SQUARE_FILM_STRIP_TILE_HEIGHT_FRACTION, usableH - screenH - gap);

  const hGaps = Math.max(count - 1, 0) * tileGap;
  let tileW = (usableW - hGaps) / count;
  // For a single speaker, also cap width at 16:9 so the tile isn't wider than it is tall.
  if (count === 1) {
    tileW = Math.min(tileW, maxTileH * TARGET_TILE_AR);
  }
  // Tiles never exceed square (height ≤ width).
  const tileH = Math.min(maxTileH, tileW);

  const rowW = count * tileW + hGaps;
  const rowLeft = margin + (usableW - rowW) / 2;

  const stackH = screenH + gap + tileH;
  const stackTop = margin + Math.max(0, (usableH - stackH) / 2);
  const stripTop = stackTop + screenH + gap;

  const tiles: TileStyle[] = [tile(stackTop, margin, usableW, screenH, W, H, radius)];

  for (let i = 0; i < count; i++) {
    tiles.push(tile(stripTop, rowLeft + i * (tileW + tileGap), tileW, tileH, W, H, radius));
  }

  return tiles;
}

/**
 * PIP – contained 16:9 screen share (letterboxed) with a small speaker overlay.
 *
 * The contained form avoids cropping ~44 % of a 16:9 feed when cover-fitted into
 * a 1:1 container. The overlay is anchored to the screen rect, not the stage, so
 * it never floats in a letterbox bar. Rectangular overlay is square (§E); circular
 * uses 28.4 % of container width.
 */
function computePip(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  pipShape: PipShape,
  pipPosition: PipPosition = 'RIGHT_BOTTOM',
  maintainScreenAspectRatio: boolean = true
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;
  const { screenTop, screenLeft, screenW, screenH } = maintainScreenAspectRatio
    ? getContainedScreenRect(W, H, margin)
    : { screenTop: margin, screenLeft: margin, screenW: W - 2 * margin, screenH: H - 2 * margin };

  const tiles: TileStyle[] = [tile(screenTop, screenLeft, screenW, screenH, W, H, radius)];

  if (pipShape === 'CIRCULAR') {
    // Uses explicit top/left (via pipRectPosition + tile()) rather than
    // auto/bottom/right/transform so the editor's selection hit-box
    // (which only reads top/left/width/height) can always find it.
    const pipW = W * 0.284;
    const pipH = pipW;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, screenTop, screenLeft, screenW, screenH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, '100%', { zIndex: 5 }));
  } else {
    // Square overlay (§E), anchored to the selected corner
    const pipW = screenW * SQUARE_PIP_WIDTH_FRACTION;
    const pipH = pipW;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, screenTop, screenLeft, screenW, screenH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, radius, { zIndex: 5 }));
  }

  return tiles;
}

/**
 * ONLY_SCREEN – screen share occupies the entire stage.
 *
 * When maintainScreenAspectRatio is true (default) the screen is letterboxed to
 * a contained 16:9 box so 16:9 media is shown whole. When false the screen fills
 * the entire usable area — used for full-bleed content such as a broadcast overlay.
 */
function computeOnlyScreen(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  maintainScreenAspectRatio: boolean
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;

  if (!maintainScreenAspectRatio) {
    const usableW = W - 2 * margin;
    const usableH = H - 2 * margin;
    return [tile(margin, margin, usableW, usableH, W, H, radius)];
  }

  const { screenTop, screenLeft, screenW, screenH } = getContainedScreenRect(W, H, margin);
  return [tile(screenTop, screenLeft, screenW, screenH, W, H, radius)];
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════

export function computeSquareLayout(params: LayoutParams): LayoutResult {
  const {
    layout,
    aspectRatio,
    speakerCount,
    showBorders,
    roundedCorners,
    pipShape = 'RECTANGULAR',
    pipPosition = 'RIGHT_BOTTOM' as const,
    maintainScreenAspectRatio = true
  } = params;

  if (aspectRatio <= 0) {
    return { tiles: [] };
  }

  const W = 100;
  const H = aspectRatio * 100;
  const count = Math.max(1, Math.min(speakerCount, 24));

  switch (layout) {
    case 'FILL':
      return { tiles: computeFill(W, H, count, showBorders, roundedCorners) };

    case 'TILES':
      return { tiles: computeTiles(W, H, count, showBorders, roundedCorners) };

    case 'SHARED_FILL':
      return { tiles: computeSharedFill(W, H, count, showBorders, roundedCorners) };

    case 'FILM_STRIP':
      return { tiles: computeFilmStrip(W, H, count, showBorders, roundedCorners) };

    case 'PIP':
      return {
        tiles: computePip(W, H, count, showBorders, roundedCorners, pipShape, pipPosition, maintainScreenAspectRatio)
      };

    case 'ONLY_SCREEN':
      return { tiles: computeOnlyScreen(W, H, count, showBorders, roundedCorners, maintainScreenAspectRatio) };

    default:
      return { tiles: [] };
  }
}
